// Reconnaître une fenêtre de RÉDACTION, et savoir si la bannière nous a ouverts.
import { SEND_AND_FILE_CONTEXT } from './sendAndFileBridge';

/**
 * Sommes-nous attachés à un message en cours de rédaction (nouveau, réponse,
 * transfert ou brouillon réouvert) ?
 *
 * LE TEST PORTE SUR LA FORME DE `subject`, et non sur la présence d'`itemId`, qui
 * serait le réflexe. En lecture, `item.subject` est une simple chaîne ; en
 * rédaction, c'est un objet `Subject` porteur de `setAsync`. C'est la différence
 * structurelle entre les deux modes, et la seule qui soit fiable ici :
 *  - `itemId` est ABSENT d'un nouveau message mais PRÉSENT sur un brouillon
 *    réouvert — il ne distingue donc pas les modes, seulement l'enregistrement ;
 *  - `getComposeTypeAsync` n'existe qu'en rédaction, mais seulement depuis
 *    l'ensemble 1.10 et pas sur les rendez-vous.
 */
export function isComposeItem(): boolean {
  try {
    const item = Office.context?.mailbox?.item as unknown as
      | { subject?: unknown }
      | undefined;
    if (!item) return false;
    const subject = item.subject as { setAsync?: unknown } | string | null | undefined;
    return (
      typeof subject === 'object' &&
      subject !== null &&
      typeof subject.setAsync === 'function'
    );
  } catch {
    // Hôte sans mailbox (banc d'essai, dialogue isolé) : ce n'est pas une
    // rédaction, et surtout ce n'est pas une raison d'empêcher le volet de
    // s'afficher.
    return false;
  }
}

/**
 * Referme la fenêtre de rédaction en abandonnant son contenu.
 *
 * À N'APPELER QU'EN DERNIER, une fois l'envoi ET le rangement terminés :
 * « when the closeAsync method successfully closes and discards the current
 * message, the add-in that called it ceases running ». Appelée trop tôt, elle
 * tuerait le volet au milieu du sondage des Éléments envoyés.
 *
 * POURQUOI IL FAUT LA REFERMER. L'envoi passe par Graph, sur le brouillon
 * enregistré côté serveur — pas par Outlook. La fenêtre locale reste donc ouverte
 * sur un message qui est déjà parti et qui n'existe plus dans les Brouillons. La
 * laisser ainsi invite l'utilisateur à la refermer lui-même, avec une invite
 * « enregistrer ce brouillon ? » qui recréerait un brouillon fantôme.
 *
 * `discardItem: true` est OBLIGATOIRE, et pas une commodité : sans lui, Outlook
 * affiche l'invite d'enregistrement, et sur un brouillon existant « the call will
 * return an error ».
 *
 * POURQUOI PAS `sendAsync` (ensemble 1.15), qui enverrait par Outlook et fermerait
 * la fenêtre tout seul : il refermerait la fenêtre — donc emporterait le volet —
 * AVANT que le sondage des Éléments envoyés et le rangement aient pu aboutir. On
 * perdrait précisément ce que cette fonctionnalité promet.
 */
export async function closeComposeWindow(): Promise<void> {
  const item = Office.context?.mailbox?.item as unknown as
    | {
        closeAsync?: (
          options: { discardItem: boolean },
          cb?: (r: Office.AsyncResult<void>) => void,
        ) => void;
      }
    | undefined;
  // Ensemble 1.14. Absent sur un hôte plus ancien : on laisse la fenêtre ouverte
  // plutôt que de lever — le mail est envoyé et rangé, c'est l'essentiel.
  if (!item?.closeAsync) return;
  await new Promise<void>((resolve) => {
    item.closeAsync!({ discardItem: true }, () => resolve());
  });
}

interface InitializationContext {
  source?: string;
}

/**
 * Le volet a-t-il été ouvert PAR la bannière « Envoyer et ranger » ?
 *
 * `contextData` posé par le gestionnaire d'événement revient ici. Utile pour
 * distinguer l'ouverture par la bannière de celle par le bouton de ruban, et donc
 * pour aller droit au but dans le premier cas.
 *
 * Rend `false` — jamais une exception — quand l'API n'existe pas ou que rien n'a
 * été passé : la vue doit s'afficher dans tous les cas.
 */
export async function openedFromSendAndFileBanner(): Promise<boolean> {
  try {
    const item = Office.context?.mailbox?.item as unknown as
      | {
          getInitializationContextAsync?: (
            cb: (r: Office.AsyncResult<string>) => void,
          ) => void;
        }
      | undefined;
    if (!item?.getInitializationContextAsync) return false;

    const raw = await new Promise<string>((resolve) => {
      item.getInitializationContextAsync!((r) => {
        resolve(r.status === Office.AsyncResultStatus.Succeeded ? r.value : '');
      });
    });
    if (!raw) return false;

    // Outlook rend soit la chaîne JSON, soit — depuis la version 2402 sur
    // Windows — l'objet lui-même. On accepte les deux.
    const parsed: InitializationContext =
      typeof raw === 'string' ? (JSON.parse(raw) as InitializationContext) : raw;
    return parsed?.source === SEND_AND_FILE_CONTEXT;
  } catch {
    return false;
  }
}
